import { useMemo } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { DateTime } from 'luxon';
import { Marker } from 'react-map-gl';
import { Fragment } from 'react/jsx-runtime';

import { BambiMapView } from 'src/common/BambiMapView/BambiMapView';
import { MapPin } from 'src/common/BambiMapView/MapPin';
import { PassengerRead } from 'src/common/external/bambi-api/bambiApi';
import { useRoutesRouteTemplatesTripTemplatesListQuery } from 'src/common/external/bambi-api/routesApi';

import { useFetchMultiplePassengers } from '../hooks/useFetchMultiplePassengers';

interface ExpandedRouteTemplateRowProps {
  routeTemplateId: string;
}
export function ExpandedRouteTemplateRow({
  routeTemplateId,
}: ExpandedRouteTemplateRowProps) {
  const { data: routeTripTemplates, isLoading: isLoadingRouteTripTemplates } =
    useRoutesRouteTemplatesTripTemplatesListQuery({
      routeTemplateId: routeTemplateId,
    });

  const passengerIds = useMemo(() => {
    return (
      routeTripTemplates?.results.map((matcher) => matcher.passenger_id) ?? []
    );
  }, [routeTripTemplates]);

  const { passengers, isLoading: isLoadingPassengers } =
    useFetchMultiplePassengers(passengerIds);

  const passengerIdMap = useMemo(() => {
    if (isLoadingPassengers) return {};
    return passengers.reduce((acc, passenger) => {
      acc[passenger.id] = passenger;
      return acc;
    }, {} as Record<string, PassengerRead | undefined>);
  }, [isLoadingPassengers, passengers]);

  if (isLoadingRouteTripTemplates || isLoadingPassengers) {
    return (
      <div className="flex flex-row items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (!routeTripTemplates?.results || routeTripTemplates.results.length === 0) {
    return (
      <div className="flex flex-row items-center justify-center">
        <div className="text-sm ">No route trip templates found</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[200px] w-full flex-row gap-4">
      <div className="w-1/4">
        <table className="min-w-full divide-y divide-gray-300 border-t border-t-gray-300">
          <thead className="sticky top-0 border-b-black bg-gray-50">
            <tr>
              <th
                scope="col"
                className="py-3.5 pl-3 pr-3 text-left text-sm font-semibold text-gray-900"
              >
                Passenger
              </th>
              <th
                scope="col"
                className="py-3.5 pl-3 pr-3 text-left text-sm font-semibold text-gray-900"
              >
                Pickup Time
              </th>
            </tr>
          </thead>
          <tbody>
            {routeTripTemplates.results.map((matcher) => {
              return (
                <tr key={matcher.id} className="even:bg-gray-50">
                  <td className="whitespace-nowrap py-4 pl-3 pr-3 text-sm font-medium text-gray-900">
                    {passengerIdMap[matcher.passenger_id]?.full_name ??
                      'Unable to retrieve passenger'}
                  </td>
                  <td className="whitespace-nowrap py-4 pl-3 pr-3 text-sm font-medium text-gray-900">
                    {DateTime.fromISO(
                      matcher.scheduled_pickup_time
                    ).toLocaleString(DateTime.TIME_SIMPLE)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="w-1/4">
        <BambiMapView
          mapViewProps={{
            initialViewState: {
              latitude: routeTripTemplates.results[0].pickup.latitude,
              longitude: routeTripTemplates.results[0].pickup.longitude,
              zoom: 10,
            },
          }}
        >
          {routeTripTemplates.results.map((matcher) => {
            return (
              <Fragment key={matcher.id}>
                <Marker
                  latitude={matcher.pickup.latitude}
                  longitude={matcher.pickup.longitude}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                  }}
                >
                  <MapPin />
                </Marker>
                <Marker
                  latitude={matcher.dropoff.latitude}
                  longitude={matcher.dropoff.longitude}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                  }}
                >
                  <MapPin />
                </Marker>
              </Fragment>
            );
          })}
        </BambiMapView>
      </div>
    </div>
  );
}
